import React from 'react';
import TeachEnglishPageLayout from '../components/TeachEnglishPageLayout';

const TeachingReading = () => {
  const title = 'Teaching Reading';
  const image = '/Images/reading.webp';
  const intro = 'Effective reading lessons move learners from decoding to deep comprehension. This guide covers text selection, pre/while/post frameworks, strategy instruction, and differentiation for mixed-level classes.';

  const seoContent = [
    'Reading isn\'t a single skill—it\'s a cluster of sub-skills: skimming for gist, scanning for specifics, intensive reading for detail, inferring meaning from context, recognizing text structure, and evaluating arguments. A balanced lesson targets 2–3 of these explicitly, not just \"read and answer questions.\"',
    'Choose texts at i+1 (slightly above independent level) using CEFR descriptors and vocabulary profiling tools. A 300-word B1 text should have 90–95% known words. Pre-teach only 5–7 blocking items—let learners infer the rest. Authentic vs. graded: use authentic for motivation and real-world skills, graded for confidence and strategy practice.',
    'Follow the classic three-phase framework: Pre-reading (activate schemata, predict, pre-teach key vocab) → While-reading (task before text: gist question → detail questions → inference/personal response) → Post-reading (discussion, writing response, vocabulary consolidation, strategy reflection).',
    'Teach strategies explicitly: \"Think-aloud\" modeling (\"I see \'however\' so I expect a contrast\"), graphic organizers (timelines, Venn diagrams, cause-effect charts), and metacognitive prompts (\"What helped you understand paragraph 3?\"). Strategies transfer across texts when made conscious.',
    'Differentiate without multiple texts: same text, tiered tasks. Support: gapped summary, multiple-choice, bilingual gloss. Core: open questions, inference, text structure analysis. Extend: critique, compare with another text, rewrite from another perspective. Everyone engages with the same content at their challenge level.',
  ];

  const levels = [
    {
      level: 'frameworks',
      label: 'Lesson Frameworks',
      tagline: 'Proven reading lesson structures',
      cards: [
        {
          title: 'PDP (Pre-During-Post) Template',
          image: '/Images/grammar1.webp',
          desc: 'Fill-in planner with strategy checkboxes, timing, and differentiation columns.',
          links: [{ text: 'Download', to: '/lesson-plans/reading-pdp' }],
        },
        {
          title: 'Reciprocal Teaching Cycle',
          image: '/Images/2people1.webp',
          desc: 'Predict → Clarify → Question → Summarize; student-led in small groups.',
          links: [{ text: 'View Guide', to: '/lesson-plans/reciprocal-teaching' }],
        },
        {
          title: 'Jigsaw Reading Structure',
          image: '/Images/vocabulary.webp',
          desc: 'Home/expert groups for long texts; each student teaches their section.',
          links: [{ text: 'View Guide', to: '/lesson-plans/jigsaw-reading' }],
        },
      ],
    },
    {
      level: 'strategies',
      label: 'Strategy Instruction',
      tagline: 'Make skills transferable',
      cards: [
        {
          title: 'Skimming & Scanning Drills',
          image: '/Images/online-teacher.webp',
          desc: 'Timed tasks with authentic texts (menus, ads, articles) + answer keys.',
          links: [{ text: 'Practice', to: '/reading-exercises/skimming-scanning' }],
        },
        {
          title: 'Inference & Context Clues',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'Step-by-step: word-part analysis, syntactic clues, discourse markers.',
          links: [{ text: 'View Module', to: '/reading-exercises/inference' }],
        },
        {
          title: 'Critical Reading Checklist',
          image: '/Images/3people1a.webp',
          desc: 'Author purpose, bias, evidence quality, counter-arguments—student rubric.',
          links: [{ text: 'Download', to: '/resources/critical-reading.pdf' }],
        },
      ],
    },
    {
      level: 'resources',
      label: 'Ready-to-Teach Reading Lessons',
      tagline: 'Texts + tasks + keys',
      cards: [
        {
          title: 'A2: Daily Routines Blog Post',
          image: '/Images/reading.webp',
          desc: '300 words, present simple focus, comprehension + vocab + writing extension.',
          links: [{ text: 'Download Pack', to: '/reading-exercises/a2-daily-routines' }],
        },
        {
          title: 'B1: Climate Change Explainer',
          image: '/Images/reading.webp',
          desc: '450 words, cause-effect structure, graphic organizer + summary writing.',
          links: [{ text: 'Download Pack', to: '/reading-exercises/b1-climate' }],
        },
        {
          title: 'B2: Op-Ed on Remote Work',
          image: '/Images/reading.webp',
          desc: '600 words, argument structure, critical questions + debate prompts.',
          links: [{ text: 'Download Pack', to: '/reading-exercises/b2-remote-work' }],
        },
      ],
    },
  ];

  return (
    <TeachEnglishPageLayout
      title={title}
      image={image}
      intro={intro}
      seoContent={seoContent}
      levels={levels}
      seoTitle="Teaching Reading Skills: Frameworks, Strategies & Differentiated Lessons"
      seoDescription="Complete guide to teaching ESL reading. Text selection criteria, PDP/reciprocal/jigsaw frameworks, explicit strategy instruction (skimming, inference, critical reading), and free downloadable lesson packs for A2–B2."
    />
  );
};

export default TeachingReading;